// Imports
import Vue from 'vue';
import Router from 'vue-router';
import AppService from '@/services/app';
import AuthService from '@/services/auth';
import Store from '@/store';

Vue.use(Router);

const authTakeOverProps = {
  variant: 'center', // center, sidebar-left, sidebar-right
  backgroundPath: require(`/src/assets/background.jpg`),
  title: '', // this will be set per page
  headerStyle: 'banner', // banner, rows, fixed
  titleColor: 'primary', // supports vuetify & # hex values, only required when using headerStyle: 'banner'
  headerColor: 'white', // supports vuetify & # hex values, only required when using headerStyle: 'banner'
  logoPath: require(`/src/assets/large_logo.png`), // image path
  copyrightText: process.env.VUE_APP_COPYRIGHT, // text
  copyrightColor: 'white', // supports vuetify & # hex values
  copyrightLocation: 'tight', // tight = under content, footer = page bottom, empty / missing will not display copyright
  menuItems: [
    {
      label: 'Register',
      routeName: 'Register',
      icon: 'register',
    },
    {
      label: 'Login',
      routeName: 'Login',
      icon: 'login',
    },
    {
      label: 'Forgotten Password',
      routeName: 'Forgotten Password',
      icon: 'password',
    },
  ],
};

const completeRegistrationTakeOverProps = {
  variant: 'sidebar-right', // center, sidebar-left, sidebar-right
  containerWidth: '65%', // any percentage value - eg. 33.333% , 50%, 75%
  backgroundPath: require(`/src/assets/background.jpg`),
  title: '', // this will be set per page
  headerStyle: 'fixed', // banner, rows, fixed
  desktopBreakpoint: 'xl-only',
  mobileContentWidth: '85%',
  copyrightText: process.env.VUE_APP_COPYRIGHT, // text
  copyrightColor: 'white', // supports vuetify & # hex values
  copyrightLocation: 'footer', // tight = under content, footer = page bottom, empty / missing will not display copyright
};

// create object router with the valid initialization
const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior: (to, from, savedPosition) => {
    if (to.hash) {
      return { selector: to.hash };
    }
    if (savedPosition) {
      return savedPosition;
    }

    return { x: 0, y: 0 };
  },
  routes: [
    // --- LOGIN PAGE ---
    {
      path: '/login',
      component: () => import('@/layouts/page/TakeOver.vue'),
      props: () => { authTakeOverProps.title = '<span class="accent--text">Log</span> <span class="secondary--text">in</span>'; return authTakeOverProps; },
      children: [
        {
          name: 'Login',
          path: '',
          component: () => import('@/views/Login.vue'),
        },
      ],
    },

    // --- REGISTRATION PAGE ---
    {
      path: '/register',
      component: () => import('@/layouts/page/TakeOver.vue'),
      props: () => { 
        completeRegistrationTakeOverProps.title = '<span class="accent--text">Register</span>'; 
        return completeRegistrationTakeOverProps; 
      },
      children: [
        {
          name: 'Register',
          path: '',
          component: () => import('@/views/Register.vue'),
        },
      ],
    },
    
    // --- FORGOTTEN PASSWORD PAGE ---
    {
      path: '/forgot',
      component: () => import('@/layouts/page/TakeOver.vue'),
      props: () => { authTakeOverProps.title = '<span class="accent--text">Forgotten</span> <span class="secondary--text">Password</span>'; return authTakeOverProps; },
      children: [
        {
          name: 'Forgotten Password',
          path: '',
          component: () => import('@/views/Password_Forgot.vue'),
        },
      ],
    },
    
    // --- SET PASSWORD PAGE ---
    {
      path: '/reset',
      component: () => import('@/layouts/page/TakeOver.vue'),
      props: () => { authTakeOverProps.title = '<span class="accent--text">Set</span> <span class="secondary--text">Password</span>'; return authTakeOverProps; },
      children: [
        {
          name: 'Set Password',
          path: ':token?',
          component: () => import('@/views/Password_Set.vue'),
        },
      ],
    },

    // --- VERIFY EMAIL PAGE ---
    {
      path: '/verify',
      component: () => import('@/layouts/page/TakeOver.vue'),
      props: authTakeOverProps,
      children: [
        {
          name: 'Verify',
          path: ':token?',
          component: () => import('@/views/Verify.vue'),
        },
      ],
    },

    // --- LOGGED IN PAGES - COMPLETE REGISTRATION ---
    {
      path: '/complete-registration',
      component: () => import('@/layouts/page/TakeOver.vue'),
      props: () => { 
        completeRegistrationTakeOverProps.title = '<span class="accent--text">Complete</span> <span class="secondary--text">Registration</span>';
        completeRegistrationTakeOverProps.desktopBreakpoint = 'lg-and-up';
        completeRegistrationTakeOverProps.mobileContentWidth = '85%';
        return completeRegistrationTakeOverProps;
      },
      children: [
        {
          name: 'Complete Registration',
          path: '',
          meta: {
            requiresAuth: true,
          },
          component: () => import('@/views/CompleteRegistration.vue'),
        },
      ],
    },

    // --- LOGGED IN PAGES - PAYMENT PACKAGE SELECTION ---
    {
      path: '/package-selection',
      component: () => import('@/layouts/page/TakeOver.vue'),
      props: () => { 
        completeRegistrationTakeOverProps.title = '<span class="accent--text">Package</span> <span class="secondary--text">Selection</span>'; 
        completeRegistrationTakeOverProps.desktopBreakpoint = 'xl-only';
        completeRegistrationTakeOverProps.mobileContentWidth = '95%';
        return completeRegistrationTakeOverProps;
      },
      children: [
        {
          name: 'Package Selection',
          path: '',
          meta: {
            requiresAuth: true,
          },
          component: () => import('@/views/PackageSelection.vue'),
        },
      ],
    },

    // --- LOGGED IN PAGES - AWAITING VERIFICATION ---
    {
      path: '/awaiting-verification',
      component: () => import('@/layouts/page/TakeOver.vue'),
      props: () => { 
        completeRegistrationTakeOverProps.title = '<span class="accent--text">Awaiting</span> <span class="secondary--text">Verification</span>'; 
        completeRegistrationTakeOverProps.desktopBreakpoint = 'lg-and-up';
        completeRegistrationTakeOverProps.mobileContentWidth = '65%';
        return completeRegistrationTakeOverProps; 
      },
      children: [
        {
          name: 'Awaiting Verification',
          path: '',
          meta: {
            requiresAuth: true,
          },
          component: () => import('@/views/AwaitingVerification.vue'),
        },
      ],
    },

    // --- LOGGED IN PAGES - AWAITING PAYMENT ---
    {
      path: '/awaiting-payment',
      component: () => import('@/layouts/page/TakeOver.vue'),
      props: () => { 
        completeRegistrationTakeOverProps.title = '<span class="accent--text">Awaiting</span> <span class="secondary--text">Payment</span>'; 
        completeRegistrationTakeOverProps.desktopBreakpoint = 'lg-and-up';
        completeRegistrationTakeOverProps.mobileContentWidth = '65%';
        return completeRegistrationTakeOverProps; 
      },
      children: [
        {
          name: 'Awaiting Payment',
          path: '',
          meta: {
            requiresAuth: true,
          },
          component: () => import('@/views/AwaitingPayment.vue'),
        },
      ],
    },

    // --- LOGGED IN PAGES ---
    {
      path: '',
      component: () => import('@/layouts/page/Dashboard.vue'),
      props: {
        drawerLogo: require(`/src/assets/large_logo.png`),
        drawerLogoMobile: require(`/src/assets/small_logo.png`),
        title: 'Overview',
        allowToggle: false,
        defaultOpen: false, //!!TODO check why this setting is not making the mobile nav close on load
        sideMenuConfig: {
          theme: {
            backgroundColor: 'white',
            textColor: 'black',
            hoverColor: 'accent',
            hoverTextColor: 'white',
            activeColor: 'secondary',
            activeTextColor: 'white',
            childArrowColor: 'black',
          },
          items: [
            {
              label: 'Overview',
              routeName: 'Overview',
              icon: 'overview',
            },
            {
              label: 'Company Profile',
              routeName: 'Customer Profile',
              icon: 'editCustomer',
            },
            {
              label: 'Review Settings',
              routeName: 'Review Settings',
              icon: 'reviewSettings',
            },
            {
              label: 'Review List',
              routeName: 'Reviews',
              icon: 'reviews',
            },
            {
              label: 'Survey QR Code',
              routeName: 'Survey QR Code',
              icon: 'qrCode',
            },
            {
              label: 'Surveys',
              routeName: 'Surveys',
              icon: 'surveys',
            },
            {
              label: 'Service Providers',
              routeName: 'Service Providers',
              icon: 'search',
              displayCondition: 'users/displayServiceProviderMenuItem',
            },
          ],
        },
        topMenuConfig: {
          theme: {
            backgroundColor: 'secondary',
            textColor: 'white',
            hoverColor: 'white',
            hoverTextColor: 'secondary',
            activeColor: 'accent',
            activeTextColor: 'white',
            childArrowColor: 'white',
          },
          items: [
            {
              label: 'users/loggedInUserDisplayName', // fetch this value from a store getter
              routeName: false,
              icon: 'profile',
              priority: true,
            },
            {
              label: 'users/loggedInUserCompanyName', // fetch this value from a store getter
              routeName: false,
              icon: 'company',
              priority: true,
            },
            {
              label: 'LOGOUT',
              routeName: false,
              customEvent: 'SITE-LOGOUT',
              icon: 'logout',
            },
          ],
        },
      },
      children: [
        {
          name: 'Overview',
          path: '',
          meta: {
            requiresAuth: true,
          },
          component: () => import('@/views/Home.vue'),
        },
        {
          name: 'Customer Profile',
          path: 'profile',
          meta: {
            requiresAuth: true,
          },
          component: () => import('@/views/CustomerProfile.vue'),
        },
        {
          name: 'Reviews',
          path: 'reviews',
          meta: {
            requiresAuth: true,
          },
          component: () => import('@/views/Reviews.vue'),
        },
        {
          name: 'Surveys',
          path: 'surveys',
          meta: {
            requiresAuth: true,
          },
          component: () => import('@/views/Surveys.vue'),
        },
        {
          name: 'Review Settings',
          path: 'review/settings',
          meta: {
            requiresAuth: true,
          },
          component: () => import('@/views/ReviewSettings.vue'),
        },
        {
          name: 'Survey QR Code',
          path: 'survey/settings',
          meta: {
            requiresAuth: true,
          },
          component: () => import('@/views/SurveySettings.vue'),
        },
        {
          name: 'Service Providers',
          path: 'search',
          meta: {
            requiresAuth: true,
          },
          component: () => import('@/views/Suppliers.vue'),
        },

        // --- CUSTOM SURVEY PAGES ---
        {
          name: 'Accommodation Survey',
          path: '/survey/accommodation/1/:id',
          meta: {
            requiresAuth: true,
          },
          component: () => import('@/views/surveys/Accommodation_1.vue'),
        },
        {
          name: 'Restaurant Survey',
          path: '/survey/restaurant/1/:id',
          meta: {
            requiresAuth: true,
          },
          component: () => import('@/views/surveys/Restaurant_1.vue'),
        },
        {
          name: 'Supplier Survey',
          path: '/survey/supplier/1/:id',
          meta: {
            requiresAuth: true,
          },
          component: () => import('@/views/surveys/Supplier_1.vue'),
        },
      ],
    },

    // --- ERROR PAGE ---
    {
      path: '/error',
      component: () => import('@/layouts/page/TakeOver.vue'),
      props: () => { authTakeOverProps.menuItems = []; return authTakeOverProps; },
      children: [
        {
          name: 'Error',
          path: '',
          component: () => import('@/views/Error.vue'),
        },
      ],
    },
  ],
});

// add the beforeEach hook
router.beforeEach((to, from, next) => {
  if (to.matched.some((record) => record.meta.requiresAuth)) {
    // check to see if the user is logged in
    // just call the checkAuth method. It will handle things if User is not Authed.
    AuthService.checkAuth().then((response) => {
      // if user status id >= 2 (Email Address Verified)
      // if customer status id === 1 (New Registration), stay on login, they need to verify their email address
      // if customer status id === 2 (User Email Address Verified), send them to Complete Payment
      // if customer status id === 3 (Completed Customer Details), send them to Package Selection
      // if customer status id === 4 (Awaiting Verification), send them to Awaiting Payment
      // if customer status id === 5 (Awaiting Payment), send them to Awaiting Payment
      // if customer status id === 6 (Registration Complete), send them to Home
      switch (Store.getters['users/loggedInUser'].customerStatusId) {
        case 1:            
          if (to.path !== '/login') {
            AppService.errorHandler('Please first verify the Email Address that you Registered with.');
            next('/login');
          } else {
            next();
          }
          break;

        case 2:            
          if (to.path !== '/complete-registration') {
            next('/complete-registration');
          } else {
            next();
          }
          break;

        case 3:
          if (to.path !== '/package-selection') {
            next('/package-selection');
          } else {
            next();
          }
          break;
          
        case 4:
          if (to.path !== '/awaiting-verification') {
            next('/awaiting-verification'); 
          } else {
            next();
          }
          break;

        case 5:
          if (to.path !== '/awaiting-payment') {
            next('/awaiting-payment'); 
          } else {
            next();
          }
          break;

        default:
          next();
          break;
      }
    });
  } else {
    next();
  }
});

// export router as default
export default router;
